import React from 'react';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { ProductDetailBlock, sortedUomRows, getCurrentSchedulePrice, getDerivedRrp } from './productReportShared';
import { downloadCsv } from '../../../shared/csv';

function buildDetailCsvRows(products, trackRrp, defaultTaxRate, anchorName) {
  const rows = [];
  products.forEach((product) => {
    const uomRows = sortedUomRows(product);
    const hasSupplierCost = product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined;
    const costAud = hasSupplierCost
      ? Number(product.cost_price_foreign) * Number(product.supplier.exchange_rate)
      : product.cost_price;
    const rrp = getDerivedRrp(product, trackRrp, defaultTaxRate, anchorName);

    const uomRowsOrPlaceholder = uomRows.length ? uomRows : [null];
    uomRowsOrPlaceholder.forEach((uomRow) => {
      rows.push({
        SKU: product.sku,
        Name: product.name,
        Category: product.product_category?.name || '',
        'Product Type': product.product_type?.code || '',
        Supplier: product.supplier?.name || '',
        'Supplier Currency': product.supplier?.currency_code || '',
        'Supplier Exchange Rate': product.supplier ? Number(product.supplier.exchange_rate).toFixed(6) : '',
        'Supplier Active': product.supplier ? (product.supplier.is_active ? 'Yes' : 'No') : '',
        'Cost Currency': hasSupplierCost ? product.supplier.currency_code : 'AUD',
        'Cost (Foreign)': hasSupplierCost ? Number(product.cost_price_foreign).toFixed(2) : '',
        'Cost (AUD)': costAud !== null && costAud !== undefined ? Number(costAud).toFixed(2) : '',
        [`${rrp.heading} ${rrp.gstSuffix}`]: rrp.value !== null && rrp.value !== undefined ? Number(rrp.value).toFixed(2) : '',
        'UoM Code': uomRow?.uom?.code || '',
        'UoM Name': uomRow?.uom?.name || '',
        'Is Base UoM': uomRow ? (String(uomRow.uom_id) === String(product.base_uom_id) ? 'Yes' : 'No') : '',
        'Conversion Rate': uomRow ? Number(uomRow.conversion_rate) : '',
        'UoM Price': (() => {
          const price = uomRow ? getCurrentSchedulePrice(uomRow.price_schedule) : null;
          return price !== null && price !== undefined ? Number(price).toFixed(2) : '';
        })(),
        'UoM Control Type': uomRow?.uom_control_type ? `${uomRow.uom_control_type.code} — ${uomRow.uom_control_type.name}` : '',
        'Can Sell': uomRow ? (uomRow.can_sell ? 'Yes' : 'No') : '',
        'Can Buy': uomRow ? (uomRow.can_buy ? 'Yes' : 'No') : '',
        Tracked: uomRow ? (uomRow.can_track ? 'Yes' : 'No') : '',
        Barcode: uomRow?.barcode || '',
      });
    });
  });
  return rows;
}

// Multi-product print/CSV report for the Product List screen (story #78).
// Each product reuses the same ProductDetailBlock as the single-product
// Report view; blocks flow continuously (no forced page break between
// products) but never split across a page break themselves.
function ProductDetailReportView({ products, loading, filterSummary, trackRrp, defaultTaxRate, anchorName, onClose }) {
  const isEmpty = !loading && products.length === 0;

  return (
    <ReportShell
      title="Product Detail Report"
      subtitle={(
        <>
          {/* Screen-only here — printed via footerNote below, in the same
              running-footer box as "Commercial in Confidence" (same split
              the Pricelist reports use), so it doesn't compete with the
              title/filter line for room at the top of a printed page. */}
          <div className="no-print">{`${formatReportTimestamp()} · ${products.length} product${products.length === 1 ? '' : 's'}`}</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      footerNote={`${formatReportTimestamp()} · ${products.length} product${products.length === 1 ? '' : 's'}`}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('product-detail-report.csv', buildDetailCsvRows(products, trackRrp, defaultTaxRate, anchorName))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No products match the current filters.</div>
      ) : (
        // Table wrapper exists only so <thead>/<tfoot> reserve clearance
        // from the fixed running header/footer on every physical page —
        // same trick the Pricelist reports use (see App.css's
        // .print-running-header comment). A plain stack of <div>s only gets
        // that clearance once, from .print-area's own padding-top, so on a
        // multi-page report every product block after the first page
        // collided with the running header/footer.
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr className="print-only-row"><td style={{ height: 18, padding: 0, border: 'none' }} /></tr>
          </thead>
          <tfoot>
            <tr className="print-only-row"><td style={{ height: 40, padding: 0, border: 'none' }} /></tr>
          </tfoot>
          <tbody>
            {products.map((product, idx) => (
              <tr key={product.id} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <td
                  style={{
                    paddingBottom: 24,
                    borderBottom: idx < products.length - 1 ? reportStyle.tableHeaderBorder : 'none',
                  }}
                >
                  <ProductDetailBlock product={product} nameFontSize={16} trackRrp={trackRrp} defaultTaxRate={defaultTaxRate} anchorName={anchorName} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportShell>
  );
}

export default ProductDetailReportView;
